import styled, { css, keyframes } from 'styled-components';
import React, { useEffect, useRef, useState } from 'react';
import { appearanceAnimation, basicColor, font, radius, transition } from '@/styles/template';
import { useContainerDimensions } from '@/core/hooks/useContainerDimensions';

type WrapPropsType = {
    ref?: any;
    margin?: any;
    arrowDown?: boolean;
    arrowUp?: boolean;
    children?: any;
    centered?: boolean;
    visible: boolean;
};

const Wrap = styled.div<WrapPropsType & { topValue: number }>`
    position: absolute;
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
    width: max-content;
    top: ${({ topValue }) => `${topValue}px`};
    z-index: 10;
    ${font.text.captionSmall};
    color: ${basicColor.deepDark};
    background: ${basicColor.accent};
    padding: 1.1rem 2rem 1rem;
    border-radius: ${radius.normal};
    display: flex;
    align-items: center;
    min-height: 5rem;
    margin-bottom: 1rem;
    animation-name: ${({ visible }) => (visible ? appearanceAnimation : '')};
    animation-duration: 0.3s;
    animation-fill-mode: forwards;
    
    & .colorFill, & .colorStroke {
        fill: ${basicColor.deepDark};
    }

    ${({ centered, margin }) => centered  && `
        transform: translateX(-50%);
        margin-left: ${margin}px;
        left: 50%;
    `}
`;

const arrowUpAnimation = keyframes`
    0% { transform: rotate(0) translateY(10px); opacity: 0; }
    100% { transform: rotate(45deg) translateY(0); opacity: 1; }
`;

const arrowDownAnimation = keyframes`
    0% { transform: rotate(0deg) translateY(-10px); opacity: 0; }
    100% { transform: rotate(45deg) translateY(0); opacity: 1; }
`;

const sharedArrowStyles = css<{visible: boolean}>`
    position: absolute;
    z-index: -1;
    width: 2rem;
    height: 2rem;
    border-radius: 2px;
    left: 0;
    right: 0;
    margin: auto;
    background: ${basicColor.accent};
    animation-duration: 0.3s;
    animation-fill-mode: forwards;
    opacity: ${({ visible }) => (visible ? 1 : 0)};
    visibility: ${({ visible }) => (visible ? 'visible' : 'hidden')};
`;

const ArrowUp = styled.div<{visible: boolean}>`
    ${sharedArrowStyles};
    animation-name: ${({ visible }) => visible ? arrowUpAnimation : 'none'};
    top: -2px;
`;

const ArrowDown = styled.div<{visible: boolean}>`
    ${sharedArrowStyles};
    animation-name: ${({ visible }) => visible ? arrowDownAnimation : 'none'};
    bottom: -2px;
`;

const Tooltip: React.FC<WrapPropsType> = (props) => {
    const {
        children,
        centered ,
        arrowDown = false,
        arrowUp = false,
        visible = false,
        ...restProps
    } = props;

    const componentRef = useRef<HTMLDivElement>(null);
    const parentRef = componentRef.current?.parentElement;

    const { width, height } = useContainerDimensions(componentRef);
    const [topValue, setTopValue] = useState<number>(0);

    let margin = -width / 2;

    useEffect(() => {
        if (parentRef) {
            const parentHeight = parentRef.getBoundingClientRect().height;
            if (arrowDown) {
                setTopValue(-(height + 8));
            } else if (arrowUp) {
                setTopValue(parentHeight + 8);
            }
        }
    }, [arrowDown, arrowUp, parentRef]);

    return (
        <Wrap ref={componentRef} margin={margin} centered={centered} topValue={topValue} visible={visible} {...restProps}>
            <ArrowUp visible={visible && arrowUp} />
            <ArrowDown visible={visible && arrowDown} />
            {children}
        </Wrap>
    );
};

export default Tooltip;
